import Swiper from './swiper.min.js';

(() => {
  'use strict';

  if (window.__themeProductBundleInitialized) return;
  window.__themeProductBundleInitialized = true;

  const ROOT_SELECTOR = '[data-product-bundle]';
  const ITEM_SELECTOR = '[data-bundle-item]';
  const OPTION_INPUT_SELECTOR = '[data-bundle-option-index][data-bundle-option-value]';
  const INCLUDE_INPUT_SELECTOR = '[data-bundle-include]';
  const TOGGLE_BUTTON_SELECTOR = '[data-bundle-toggle-button]';
  const QUANTITY_CONTROL_SELECTOR = '[data-bundle-quantity]';
  const QUANTITY_DECREASE_SELECTOR = '[data-bundle-qty-decrease]';
  const QUANTITY_INCREASE_SELECTOR = '[data-bundle-qty-increase]';
  const QUANTITY_COUNT_SELECTOR = '[data-bundle-qty-count]';
  const SUBMIT_SELECTOR = '[data-bundle-submit]';
  const SUMMARY_REMOVE_SELECTOR = '[data-bundle-selected-remove]';
  const VARIANT_TRIGGER_SELECTOR = '[data-bundle-variant-trigger]';
  const VARIANT_DIALOG_SELECTOR = '[data-bundle-variant-dialog]';
  const DIALOG_CLOSE_SELECTOR = '[data-bundle-dialog-close]';
  const DIALOG_CONFIRM_SELECTOR = '[data-bundle-dialog-confirm]';
  const controllers = new Map();

  /* ── Scroll-lock helpers (mirrors theme-ui.js pattern) ─────────── */
  const SCROLLBAR_VAR = '--theme-scrollbar-compensation';
  const GLOBAL_LOCK_CLASS = 'theme-scroll-lock-active';
  const GLOBAL_LOCK_ATTR = 'data-theme-scroll-lock-count';

  const acquireScrollLock = () => {
    const root = document.documentElement;
    const count = parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10);
    if (count <= 0) {
      const compensation = `${Math.max(0, window.innerWidth - root.clientWidth)}px`;
      root.style.setProperty(SCROLLBAR_VAR, compensation);
      document.body.style.setProperty(SCROLLBAR_VAR, compensation);
      root.classList.add(GLOBAL_LOCK_CLASS);
      document.body.classList.add(GLOBAL_LOCK_CLASS);
    }
    root.setAttribute(GLOBAL_LOCK_ATTR, String(count + 1));
  };

  const releaseScrollLock = () => {
    const root = document.documentElement;
    const count = Math.max(0, parseInt(root.getAttribute(GLOBAL_LOCK_ATTR) || '0', 10));
    const next = Math.max(0, count - 1);
    if (next === 0) {
      root.classList.remove(GLOBAL_LOCK_CLASS);
      document.body.classList.remove(GLOBAL_LOCK_CLASS);
      root.removeAttribute(GLOBAL_LOCK_ATTR);
      root.style.removeProperty(SCROLLBAR_VAR);
      document.body.style.removeProperty(SCROLLBAR_VAR);
    } else {
      root.setAttribute(GLOBAL_LOCK_ATTR, String(next));
    }
  };

  /* WeakMap to store per-dialog cancel handlers for clean removal */
  const dialogCancelHandlers = new WeakMap();

  const parseIntSafe = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseFloatSafe = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseBoolean = (value) => String(value) === 'true';
  const sanitizeOptionValue = (value) => String(value || '').trim();

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  class ProductBundleController {
    constructor(root) {
      this.root = root;
      this.items = [];
      this.itemState = new WeakMap();

      this.summaryCountEls = [];
      this.summaryTotalEls = [];
      this.summaryFinalEls = [];
      this.summaryCompareEls = [];
      this.summarySavingsEls = [];
      this.summaryCompareRows = [];
      this.summarySavingsRows = [];
      this.submitButtons = [];
      this.statusEl = null;

      this.selectedListEl = null;
      this.selectedPaginationEl = null;
      this.progressFillEl = null;
      this.progressTierEls = [];
      this.offerStepsEl = null;
      this.offerGeometryRaf = 0;
      this.selectedSwiper = null;
      this.hasRenderedSummaryOnce = false;
      this.previousSelectedProductIds = new Set();
      this.itemsSliderEl = null;
      this.itemsSwiper = null;
      this.itemsPrevEl = null;
      this.itemsNextEl = null;
      this.itemsSlidesMobile = 1.14;
      this.itemsSlidesDesktop = 3.14;
      this.itemsGapMobile = 12;
      this.itemsGapDesktop = 16;
      this.enableCarouselMobile = true;
      this.enableCarouselDesktop = false;
      this.itemsColumnsMobile = 2;
      this.itemsColumnsDesktop = 3;
      this.itemsSwiperViewportMode = '';

      this.mobileSummaryEl = null;
      this.mobileSummaryObserver = null;
      this.mobileSummaryMql = null;

      this.minimumRequired = 1;
      this.showCompareAtTotal = false;
      this.showSavings = false;
      this.totalProducts = 0;
      this.moneyFormat = "${{amount}}";
      this.cartType = 'drawer';
      this.cartUrl = '/cart';
      this.routesRoot = '/';

      this.minItemsMessage = '';
      this.addedMessage = '';
      this.addingMessage = '';
      this.errorMessage = 'Unable to add bundle to cart right now.';
      this.includeLabel = 'Add';
      this.soldOutLabel = 'Sold out';

      this.hasAttemptedSubmit = false;
      this.isSubmitting = false;

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onMobileViewportChange = this.onMobileViewportChange.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
      this.onSelectedListScroll = this.onSelectedListScroll.bind(this);
    }

    init() {
      this.items = Array.from(this.root.querySelectorAll(ITEM_SELECTOR));
      if (!this.items.length) return false;

      this.summaryCountEls = Array.from(this.root.querySelectorAll('[data-bundle-summary-count]'));
      this.summaryTotalEls = Array.from(this.root.querySelectorAll('[data-bundle-summary-total]'));
      this.summaryFinalEls = Array.from(this.root.querySelectorAll('[data-bundle-summary-final]'));
      this.summaryCompareEls = Array.from(this.root.querySelectorAll('[data-bundle-summary-compare]'));
      this.summarySavingsEls = Array.from(this.root.querySelectorAll('[data-bundle-summary-savings]'));
      this.summaryCompareRows = Array.from(this.root.querySelectorAll('[data-bundle-summary-compare-row]'));
      this.summarySavingsRows = Array.from(this.root.querySelectorAll('[data-bundle-summary-savings-row]'));
      this.submitButtons = Array.from(this.root.querySelectorAll(SUBMIT_SELECTOR));
      this.statusEl = this.root.querySelector('[data-bundle-status]');

      this.selectedListEl = this.root.querySelector('[data-bundle-selected-list]');
      this.selectedPaginationEl = this.root.querySelector('[data-bundle-selected-pagination]');
      this.progressFillEl = this.root.querySelector('[data-bundle-progress-fill]');
      this.progressTierEls = Array.from(this.root.querySelectorAll('[data-bundle-tier]'));
      this.offerStepsEl = this.root.querySelector('[data-bundle-offer-steps]');
      this.itemsSliderEl = this.root.querySelector('[data-bundle-items-slider]');
      this.itemsPrevEl = this.root.querySelector('[data-bundle-items-prev]');
      this.itemsNextEl = this.root.querySelector('[data-bundle-items-next]');

      this.mobileSummaryEl = this.root.querySelector('[data-bundle-mobile-summary]');
      this.mobileSummaryMql = window.matchMedia('(max-width: 699px)');
      this.bindSelectedListScroll();

      this.minimumRequired = Math.max(1, parseIntSafe(this.root.dataset.minimumItemsRequired, 1));
      this.showCompareAtTotal = parseBoolean(this.root.dataset.showCompareAtTotal);
      this.showSavings = parseBoolean(this.root.dataset.showSavings);
      this.totalProducts = Math.max(1, parseIntSafe(this.root.dataset.totalProducts, this.items.length));
      this.moneyFormat =
        this.root.dataset.moneyFormat ||
        window.theme?.moneyFormat ||
        window.Shopify?.money_format ||
        "${{amount}}";
      this.cartType = this.root.dataset.cartType || document.body.dataset.cartType || 'drawer';
      this.cartUrl = this.root.dataset.cartUrl || '/cart';
      this.routesRoot = window.Shopify?.routes?.root || '/';

      this.minItemsMessage = this.root.dataset.minItemsMessage || '';
      this.addedMessage = this.root.dataset.addedMessage || '';
      this.addingMessage = this.root.dataset.addingMessage || '';
      this.errorMessage = this.root.dataset.addErrorMessage || this.errorMessage;
      this.includeLabel = this.root.dataset.includeLabel || this.includeLabel;
      this.soldOutLabel = this.root.dataset.soldOutLabel || this.soldOutLabel;
      this.itemsSlidesMobile = Math.max(
        1.05,
        parseFloatSafe(this.root.dataset.bundleItemsSlidesMobile, this.itemsSlidesMobile)
      );
      this.itemsSlidesDesktop = Math.max(
        1.05,
        parseFloatSafe(this.root.dataset.bundleItemsSlidesDesktop, this.itemsSlidesDesktop)
      );
      this.itemsGapMobile = Math.max(0, parseIntSafe(this.root.dataset.bundleItemsGapMobile, this.itemsGapMobile));
      this.itemsGapDesktop = Math.max(0, parseIntSafe(this.root.dataset.bundleItemsGapDesktop, this.itemsGapDesktop));
      const mobileCarouselAttr = this.root.dataset.bundleItemsCarouselMobile;
      const desktopCarouselAttr = this.root.dataset.bundleItemsCarouselDesktop;
      this.enableCarouselMobile =
        mobileCarouselAttr == null ? true : parseBoolean(mobileCarouselAttr);
      this.enableCarouselDesktop =
        desktopCarouselAttr == null ? false : parseBoolean(desktopCarouselAttr);
      this.itemsColumnsMobile = Math.max(1, parseIntSafe(this.root.dataset.bundleItemsColumnsMobile, this.itemsColumnsMobile));
      this.itemsColumnsDesktop = Math.max(1, parseIntSafe(this.root.dataset.bundleItemsColumnsDesktop, this.itemsColumnsDesktop));

      this.items.forEach((item) => this.hydrateItem(item));

      this.root.addEventListener('change', this.onChange);
      this.root.addEventListener('click', this.onClick);
      window.addEventListener('resize', this.onWindowResize);

      if (this.mobileSummaryMql?.addEventListener) {
        this.mobileSummaryMql.addEventListener('change', this.onMobileViewportChange);
      } else if (this.mobileSummaryMql?.addListener) {
        this.mobileSummaryMql.addListener(this.onMobileViewportChange);
      }

      this.setupMobileSummaryObserver();
      this.initItemsSwiper();
      this.updateSummary({ preserveStatus: true });
      return true;
    }

    destroy() {
      // Close any open variant dialogs (skip animation on section unload)
      this.root.querySelectorAll(VARIANT_DIALOG_SELECTOR).forEach((dialog) => {
        if (dialog instanceof HTMLDialogElement && dialog.open) {
          dialog.classList.remove('is-open', 'is-closing');
          dialog.removeAttribute('data-bundle-dialog-closing');
          dialog.close();
          releaseScrollLock();
        }
      });

      this.root.removeEventListener('change', this.onChange);
      this.root.removeEventListener('click', this.onClick);
      window.removeEventListener('resize', this.onWindowResize);
      this.unbindSelectedListScroll();
      window.cancelAnimationFrame(this.offerGeometryRaf);
      this.destroySelectedSwiper();
      this.destroyItemsSwiper();

      if (this.mobileSummaryMql?.removeEventListener) {
        this.mobileSummaryMql.removeEventListener('change', this.onMobileViewportChange);
      } else if (this.mobileSummaryMql?.removeListener) {
        this.mobileSummaryMql.removeListener(this.onMobileViewportChange);
      }

      this.teardownMobileSummaryObserver();
      this.setMobileSummaryVisible(false);
    }

    onMobileViewportChange() {
      this.setupMobileSummaryObserver();
    }

    onWindowResize() {
      this.initItemsSwiper();
      this.scheduleOfferProgressGeometry();
      this.updateSelectedListFadeState();
    }

    bindSelectedListScroll() {
      if (!(this.selectedListEl instanceof HTMLElement)) return;
      this.selectedListEl.addEventListener('scroll', this.onSelectedListScroll, { passive: true });
      this.updateSelectedListFadeState();
    }

    unbindSelectedListScroll() {
      if (!(this.selectedListEl instanceof HTMLElement)) return;
      this.selectedListEl.removeEventListener('scroll', this.onSelectedListScroll);
    }

    onSelectedListScroll() {
      this.updateSelectedListFadeState();
    }

    updateSelectedListFadeState() {
      if (!(this.selectedListEl instanceof HTMLElement)) return;

      const maxScroll = this.selectedListEl.scrollHeight - this.selectedListEl.clientHeight;
      const hasOverflow = maxScroll > 1;
      const atTop = this.selectedListEl.scrollTop <= 1;
      const atBottom = this.selectedListEl.scrollTop >= maxScroll - 1;

      this.selectedListEl.classList.toggle('has-overflow', hasOverflow);
      this.selectedListEl.classList.toggle('is-at-top', atTop);
      this.selectedListEl.classList.toggle('is-at-bottom', atBottom);
    }

    scrollSelectedItemIntoView(productId) {
      if (!(this.selectedListEl instanceof HTMLElement) || !productId) return;

      const target = this.selectedListEl.querySelector(`[data-bundle-selected-product-id="${productId}"]`);
      if (!(target instanceof HTMLElement)) return;

      const prefersReducedMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      target.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }

    setupMobileSummaryObserver() {
      this.teardownMobileSummaryObserver();

      if (!this.mobileSummaryEl) return;
      if (!this.mobileSummaryMql?.matches) {
        this.setMobileSummaryVisible(false);
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.setMobileSummaryVisible(true);
        return;
      }

      this.mobileSummaryObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;

          const rect = entry.boundingClientRect;
          const visible =
            entry.isIntersecting &&
            rect.top < window.innerHeight - 64 &&
            rect.bottom > 120;

          this.setMobileSummaryVisible(visible);
        },
        {
          threshold: [0, 0.1, 0.4],
        }
      );

      this.mobileSummaryObserver.observe(this.root);
    }

    teardownMobileSummaryObserver() {
      if (!this.mobileSummaryObserver) return;
      this.mobileSummaryObserver.disconnect();
      this.mobileSummaryObserver = null;
    }

    setMobileSummaryVisible(isVisible) {
      if (!this.mobileSummaryEl) return;
      this.mobileSummaryEl.classList.toggle('is-visible', isVisible);
    }

    syncItemsListSemantics(isSwiperActive) {
      if (!this.itemsSliderEl) return;
      const itemsTrack = this.itemsSliderEl.querySelector('.product-bundle__items');
      if (!(itemsTrack instanceof HTMLElement)) return;

      if (!itemsTrack.hasAttribute('data-original-role')) {
        const originalTrackRole = itemsTrack.getAttribute('role');
        itemsTrack.setAttribute('data-original-role', originalTrackRole === null ? '' : originalTrackRole);
      }

      this.items.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;
        if (item.hasAttribute('data-original-role')) return;
        const originalItemRole = item.getAttribute('role');
        item.setAttribute('data-original-role', originalItemRole === null ? '' : originalItemRole);
      });

      if (isSwiperActive) {
        itemsTrack.setAttribute('role', 'presentation');
        return;
      }

      const originalTrackRole = itemsTrack.getAttribute('data-original-role');
      if (originalTrackRole === '') {
        itemsTrack.removeAttribute('role');
      } else if (originalTrackRole !== null) {
        itemsTrack.setAttribute('role', originalTrackRole);
      }

      this.items.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;
        const originalItemRole = item.getAttribute('data-original-role');
        if (originalItemRole === '') {
          item.removeAttribute('role');
        } else if (originalItemRole !== null) {
          item.setAttribute('role', originalItemRole);
        }
      });
    }

    scheduleOfferProgressGeometry() {
      window.cancelAnimationFrame(this.offerGeometryRaf);
      this.offerGeometryRaf = window.requestAnimationFrame(() => this.updateOfferProgressGeometry());
    }

    updateOfferProgressGeometry() {
      if (!this.offerStepsEl || !this.progressFillEl || !this.progressTierEls.length) return;

      const dots = this.progressTierEls
        .map((step) => step.querySelector('[data-bundle-offer-dot]'))
        .filter((dot) => dot instanceof HTMLElement);

      if (!dots.length) return;

      const stepsRect = this.offerStepsEl.getBoundingClientRect();
      const firstDotRect = dots[0].getBoundingClientRect();
      const lastDotRect = dots[dots.length - 1].getBoundingClientRect();

      const lineLeft = firstDotRect.left + firstDotRect.width / 2 - stepsRect.left;
      const lineRight = lastDotRect.left + lastDotRect.width / 2 - stepsRect.left;
      const lineWidth = Math.max(0, lineRight - lineLeft);
      const lineTop = firstDotRect.top + firstDotRect.height / 2 - stepsRect.top;

      this.offerStepsEl.style.setProperty('--bundle-offer-line-left', `${Math.round(lineLeft)}px`);
      this.offerStepsEl.style.setProperty('--bundle-offer-line-width', `${Math.round(lineWidth)}px`);
      this.offerStepsEl.style.setProperty('--bundle-offer-line-top', `${Math.round(lineTop)}px`);

      const hitSteps = this.progressTierEls.filter((step) => step.classList.contains('is-hit'));
      if (hitSteps.length < 2) {
        this.offerStepsEl.style.setProperty('--bundle-offer-fill-width', '0px');
        return;
      }

      const lastHitStep = hitSteps[hitSteps.length - 1];
      const lastHitDot = lastHitStep.querySelector('[data-bundle-offer-dot]');
      if (!(lastHitDot instanceof HTMLElement)) {
        this.offerStepsEl.style.setProperty('--bundle-offer-fill-width', '0px');
        return;
      }

      const lastHitRect = lastHitDot.getBoundingClientRect();
      const fillRight = lastHitRect.left + lastHitRect.width / 2 - stepsRect.left;
      const fillWidth = Math.max(0, fillRight - lineLeft);
      this.offerStepsEl.style.setProperty('--bundle-offer-fill-width', `${Math.round(fillWidth)}px`);
    }

    getItemsViewportMode() {
      return window.matchMedia('(min-width: 700px)').matches ? 'desktop' : 'mobile';
    }

    isCarouselEnabledForViewport(viewportMode) {
      if (viewportMode === 'desktop') return this.enableCarouselDesktop;
      return this.enableCarouselMobile;
    }

    getColumnsForViewport(viewportMode) {
      if (viewportMode === 'desktop') return this.itemsColumnsDesktop;
      return this.itemsColumnsMobile;
    }

    initItemsSwiper() {
      if (!this.itemsSliderEl || !Swiper) return;

      const viewportMode = this.getItemsViewportMode();
      const enableCarousel = this.isCarouselEnabledForViewport(viewportMode);
      const minimumSlidesForCarousel = this.getColumnsForViewport(viewportMode);
      const shouldInitCarousel = enableCarousel && this.items.length > minimumSlidesForCarousel;

      if (!shouldInitCarousel) {
        this.destroyItemsSwiper();
        this.itemsSwiperViewportMode = viewportMode;
        this.syncItemsListSemantics(false);
        return;
      }

      if (this.itemsSwiper && this.itemsSwiperViewportMode === viewportMode) {
        this.itemsSwiper.update();
        return;
      }

      this.destroyItemsSwiper();

      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const tabletSlidesPerView = Math.min(2.2, Math.max(1.2, this.itemsSlidesDesktop - 0.9));
      const navConfig =
        this.itemsPrevEl && this.itemsNextEl
          ? {
              prevEl: this.itemsPrevEl,
              nextEl: this.itemsNextEl,
            }
          : undefined;

      this.itemsSwiper = new Swiper(this.itemsSliderEl, {
        speed: prefersReduced ? 0 : 440,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        threshold: 8,
        slidesPerView: this.itemsSlidesMobile,
        spaceBetween: this.itemsGapMobile,
        wrapperClass: 'product-bundle__items',
        slideClass: 'product-bundle__item',
        navigation: navConfig,
        breakpoints: {
          700: {
            slidesPerView: tabletSlidesPerView,
            spaceBetween: this.itemsGapMobile,
          },
          1000: {
            slidesPerView: this.itemsSlidesDesktop,
            spaceBetween: this.itemsGapDesktop,
          },
        },
      });
      this.syncItemsListSemantics(true);
      this.itemsSwiperViewportMode = viewportMode;

      window.requestAnimationFrame(() => {
        this.itemsSwiper?.update();
      });
      window.setTimeout(() => {
        this.itemsSwiper?.update();
      }, 80);

      if (this.itemsPrevEl) this.itemsPrevEl.hidden = false;
      if (this.itemsNextEl) this.itemsNextEl.hidden = false;
    }

    destroyItemsSwiper() {
      if (!this.itemsSwiper) {
        this.syncItemsListSemantics(false);
        return;
      }
      this.itemsSwiper.destroy(true, true);
      this.itemsSwiper = null;
      this.itemsSwiperViewportMode = '';
      this.syncItemsListSemantics(false);
      if (this.itemsPrevEl) this.itemsPrevEl.hidden = true;
      if (this.itemsNextEl) this.itemsNextEl.hidden = true;
    }

    hydrateItem(item) {
      const variantsScript = item.querySelector('[data-bundle-variants]');
      let variants = [];

      if (variantsScript?.textContent) {
        try {
          const parsed = JSON.parse(variantsScript.textContent);
          if (Array.isArray(parsed)) variants = parsed;
        } catch (_) {
          variants = [];
        }
      }

      const selectedVariantId = parseIntSafe(item.dataset.selectedVariantId, 0);
      const initialVariant =
        this.findVariantById(variants, selectedVariantId) ||
        variants.find((variant) => variant.available) ||
        variants[0] ||
        null;

      const state = {
        variants,
        currentVariant: initialVariant,
      };

      this.itemState.set(item, state);
      const includeInput = item.querySelector(INCLUDE_INPUT_SELECTOR);
      const isIncluded = includeInput instanceof HTMLInputElement ? includeInput.checked : Boolean(initialVariant?.available);
      if (isIncluded) {
        this.setItemQuantity(item, 1);
      } else {
        item.removeAttribute('data-bundle-quantity-count');
      }
      this.syncOptionInputsToVariant(item, initialVariant);
      this.applyVariantState(item, state);
      this.updateVariantTriggerLabel(item);
    }

    findVariantById(variants, id) {
      if (!Array.isArray(variants) || id <= 0) return null;
      return variants.find((variant) => parseIntSafe(variant?.id, 0) === id) || null;
    }

    getVariantOptionValue(variant, index) {
      if (!variant || index < 0) return '';
      if (Array.isArray(variant.options) && variant.options[index] != null) {
        return sanitizeOptionValue(variant.options[index]);
      }

      const fallbackValue = variant[`option${index + 1}`];
      return sanitizeOptionValue(fallbackValue);
    }

    getVariantOptions(variant) {
      if (!variant) return [];

      if (Array.isArray(variant.options) && variant.options.length) {
        return variant.options.map((value) => sanitizeOptionValue(value));
      }

      const options = [];
      for (let index = 0; index < 3; index += 1) {
        const optionValue = this.getVariantOptionValue(variant, index);
        if (optionValue) options.push(optionValue);
      }
      return options;
    }

    readSelectedOptions(item) {
      const selectedMap = new Map();
      item.querySelectorAll(`${OPTION_INPUT_SELECTOR}:checked`).forEach((input) => {
        const optionIndex = parseIntSafe(input.dataset.bundleOptionIndex, -1);
        if (optionIndex < 0) return;
        selectedMap.set(optionIndex, sanitizeOptionValue(input.dataset.bundleOptionValue || input.value));
      });

      if (!selectedMap.size) return [];

      const maxIndex = Math.max(...selectedMap.keys());
      return Array.from({ length: maxIndex + 1 }, (_, index) => selectedMap.get(index) || '');
    }

    findVariantByExactOptions(variants, selectedOptions) {
      if (!Array.isArray(variants) || !variants.length || !selectedOptions.length) return null;

      return (
        variants.find((variant) => {
          const variantOptions = this.getVariantOptions(variant);
          if (variantOptions.length < selectedOptions.length) return false;

          return selectedOptions.every((selectedOption, index) => {
            if (!selectedOption) return true;
            return variantOptions[index] === selectedOption;
          });
        }) || null
      );
    }

    findVariantForChangedOption(variants, optionIndex, optionValue) {
      if (!Array.isArray(variants) || !variants.length || optionIndex < 0 || !optionValue) return null;

      const availableMatch = variants.find(
        (variant) => variant.available && this.getVariantOptionValue(variant, optionIndex) === optionValue
      );
      if (availableMatch) return availableMatch;

      return variants.find((variant) => this.getVariantOptionValue(variant, optionIndex) === optionValue) || null;
    }

    syncOptionInputsToVariant(item, variant) {
      if (!variant) return;

      const variantOptions = this.getVariantOptions(variant);
      if (!variantOptions.length) return;

      item.querySelectorAll(OPTION_INPUT_SELECTOR).forEach((input) => {
        const optionIndex = parseIntSafe(input.dataset.bundleOptionIndex, -1);
        if (optionIndex < 0) return;
        const optionValue = sanitizeOptionValue(input.dataset.bundleOptionValue || input.value);
        input.checked = variantOptions[optionIndex] === optionValue;
      });
    }

    normalizeCompareAt(priceCents, compareAtCents) {
      const price = Math.max(0, parseIntSafe(priceCents, 0));
      const compare = parseIntSafe(compareAtCents, 0);
      return compare > price ? compare : price;
    }

    applyVariantState(item, state) {
      const variant = state.currentVariant;

      const priceCents = parseIntSafe(variant?.price, parseIntSafe(item.dataset.selectedPriceCents, 0));
      const compareAtCents = this.normalizeCompareAt(
        priceCents,
        parseIntSafe(variant?.compare_at_price, parseIntSafe(item.dataset.selectedCompareCents, priceCents))
      );
      const isAvailable = Boolean(variant?.available);

      item.dataset.selectedVariantId = String(parseIntSafe(variant?.id, 0));
      item.dataset.selectedPriceCents = String(priceCents);
      item.dataset.selectedCompareCents = String(compareAtCents);

      const priceEl = item.querySelector('[data-bundle-item-price]');
      if (priceEl) priceEl.innerHTML = this.formatMoney(priceCents);

      const compareEl = item.querySelector('[data-bundle-item-compare]');
      if (compareEl) {
        compareEl.innerHTML = this.formatMoney(compareAtCents);
        compareEl.hidden = compareAtCents <= priceCents;
      }

      this.updateItemImage(item, variant);
      this.syncItemControls(item, isAvailable);
      this.updateVariantTriggerLabel(item);
    }

    getImageAspectRatioValue(imageObject) {
      if (!imageObject || typeof imageObject !== 'object') return '';

      const imageWidth = parseFloatSafe(imageObject.width, 0);
      const imageHeight = parseFloatSafe(imageObject.height, 0);
      if (imageWidth > 0 && imageHeight > 0) {
        return `${imageWidth} / ${imageHeight}`;
      }

      const imageAspectRatio = parseFloatSafe(imageObject.aspect_ratio, 0);
      if (imageAspectRatio > 0) {
        return String(imageAspectRatio);
      }

      return '';
    }

    updateItemImageRatio(item, variant, variantImage = null) {
      if (!(item instanceof HTMLElement)) return;

      const ratioMode = sanitizeOptionValue(item.dataset.bundleRatioMode || '');
      const defaultRatio = sanitizeOptionValue(item.dataset.bundleRatioDefault || '1 / 1');

      if (ratioMode !== 'adapt') {
        item.style.setProperty('--bundle-item-ratio', defaultRatio);
        return;
      }

      const ratioFromVariantImage = this.getImageAspectRatioValue(
        variantImage || variant?.featured_image || variant?.featured_media?.preview_image || null
      );

      item.style.setProperty('--bundle-item-ratio', ratioFromVariantImage || defaultRatio);
    }

    updateItemImage(item, variant) {
      const variantImage = variant?.featured_image || variant?.featured_media?.preview_image || null;
      this.updateItemImageRatio(item, variant, variantImage);

      const imageEl = item.querySelector('.product-bundle__item-image');
      if (!(imageEl instanceof HTMLImageElement)) return;

      const imageSource = variantImage?.src || variantImage?.url || '';
      if (!imageSource) return;

      imageEl.setAttribute('src', imageSource);
      imageEl.removeAttribute('srcset');
      item.dataset.bundleThumbUrl = imageSource;

      if (variantImage?.alt) {
        imageEl.setAttribute('alt', variantImage.alt);
      }
    }

    syncItemControls(item, isAvailable) {
      const includeInput = item.querySelector(INCLUDE_INPUT_SELECTOR);
      const toggleButton = item.querySelector(TOGGLE_BUTTON_SELECTOR);
      const quantityControl = item.querySelector(QUANTITY_CONTROL_SELECTOR);
      const soldOutLayer = item.querySelector('[data-bundle-soldout]');

      if (includeInput) {
        if (!isAvailable) {
          includeInput.checked = false;
          includeInput.disabled = true;
        } else {
          includeInput.disabled = false;
        }
      }

      const includeChecked = includeInput ? includeInput.checked : isAvailable;
      if (includeChecked && isAvailable) {
        this.setItemQuantity(item, this.getItemQuantity(item));
      } else {
        item.removeAttribute('data-bundle-quantity-count');
        this.updateItemQuantityDisplay(item, 1);
      }

      if (toggleButton) {
        toggleButton.disabled = !isAvailable;
        toggleButton.hidden = includeChecked && isAvailable;
      }

      if (quantityControl instanceof HTMLElement) {
        quantityControl.hidden = !(includeChecked && isAvailable);
      }

      const toggleText = !isAvailable ? this.soldOutLabel : this.includeLabel;

      item.querySelectorAll('[data-bundle-toggle-label]').forEach((toggleLabel) => {
        toggleLabel.textContent = toggleText;
      });

      if (soldOutLayer instanceof HTMLElement) {
        soldOutLayer.hidden = isAvailable;
      }

      item.classList.toggle('is-selected', includeChecked);
    }

    /* ── Variant dialog ─────────────────────────────────────────── */

    openVariantDialog(dialog) {
      if (!dialog || dialog.open) return;

      const cancelHandler = (e) => {
        e.preventDefault();
        this.closeVariantDialog(dialog);
      };
      dialogCancelHandlers.set(dialog, cancelHandler);
      dialog.addEventListener('cancel', cancelHandler);

      dialog.showModal();
      acquireScrollLock();

      // Force a layout pass so the browser registers the panel's blurred/tilted
      // starting state before we add is-open and trigger the perspective transition.
      void dialog.offsetWidth;
      dialog.classList.add('is-open');
    }

    closeVariantDialog(dialog) {
      if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
      if (dialog.hasAttribute('data-bundle-dialog-closing')) return;

      dialog.setAttribute('data-bundle-dialog-closing', '');
      // Remove is-open first so CSS sees the transition from open → closing state
      dialog.classList.remove('is-open');
      dialog.classList.add('is-closing');

      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        dialog.removeAttribute('data-bundle-dialog-closing');
        dialog.classList.remove('is-closing');
        if (dialog.open) dialog.close();
        releaseScrollLock();

        const cancelHandler = dialogCancelHandlers.get(dialog);
        if (cancelHandler) {
          dialog.removeEventListener('cancel', cancelHandler);
          dialogCancelHandlers.delete(dialog);
        }
      };

      // 520 ms matches --pbvd-close-dur in the CSS
      setTimeout(settle, 520);
    }

    updateVariantTriggerLabel(item) {
      const trigger = item.querySelector(VARIANT_TRIGGER_SELECTOR);
      if (!trigger) return;
      const labelEl = trigger.querySelector('[data-bundle-variant-label]');
      if (!labelEl) return;
      const state = this.itemState.get(item);
      const variant = state?.currentVariant;
      if (!variant) return;
      const parts = this.getVariantOptions(variant).filter(Boolean);
      labelEl.textContent = parts.length ? parts.join(' / ') : (variant.title || '');
    }

    updateItemQuantityDisplay(item, quantity = null) {
      if (!(item instanceof HTMLElement)) return;
      const normalized = Math.max(1, parseIntSafe(quantity, this.getItemQuantity(item)));
      item.querySelectorAll(QUANTITY_COUNT_SELECTOR).forEach((countEl) => {
        countEl.textContent = String(normalized);
      });
    }

    setItemQuantity(item, quantity) {
      if (!(item instanceof HTMLElement)) return 1;
      const normalized = Math.max(1, parseIntSafe(quantity, 1));
      item.dataset.bundleQuantityCount = String(normalized);
      this.updateItemQuantityDisplay(item, normalized);
      return normalized;
    }

    getItemQuantity(item) {
      if (!(item instanceof HTMLElement)) return 1;
      return Math.max(1, parseIntSafe(item.dataset.bundleQuantityCount, 1));
    }

    onChange(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.matches(OPTION_INPUT_SELECTOR)) {
        const optionInput = target;
        if (!(optionInput instanceof HTMLInputElement) || !optionInput.checked) return;

        const item = optionInput.closest(ITEM_SELECTOR);
        if (!item) return;

        const state = this.itemState.get(item);
        if (!state) return;

        const optionIndex = parseIntSafe(optionInput.dataset.bundleOptionIndex, -1);
        const optionValue = sanitizeOptionValue(optionInput.dataset.bundleOptionValue || optionInput.value);
        const selectedOptions = this.readSelectedOptions(item);

        let nextVariant = this.findVariantByExactOptions(state.variants, selectedOptions);
        if (!nextVariant) {
          nextVariant = this.findVariantForChangedOption(state.variants, optionIndex, optionValue);
        }

        if (nextVariant) {
          state.currentVariant = nextVariant;
          this.syncOptionInputsToVariant(item, nextVariant);
          this.applyVariantState(item, state);
          this.updateSummary({ preserveStatus: false });
        }
        return;
      }

      if (target.matches(INCLUDE_INPUT_SELECTOR)) {
        const item = target.closest(ITEM_SELECTOR);
        if (!item) return;

        if (target instanceof HTMLInputElement) {
          if (target.checked) {
            this.setItemQuantity(item, this.getItemQuantity(item));
          } else {
            item.removeAttribute('data-bundle-quantity-count');
          }
        }

        const state = this.itemState.get(item);
        this.syncItemControls(item, Boolean(state?.currentVariant?.available));
        this.updateSummary({ preserveStatus: false });
        return;
      }
    }

    onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Variant trigger — open dialog
      const variantTrigger = target.closest(VARIANT_TRIGGER_SELECTOR);
      if (variantTrigger) {
        event.preventDefault();
        const item = variantTrigger.closest(ITEM_SELECTOR);
        if (!item) return;
        const dialog = item.querySelector(VARIANT_DIALOG_SELECTOR);
        if (!(dialog instanceof HTMLDialogElement)) return;
        this.openVariantDialog(dialog);
        return;
      }

      // Backdrop click (target IS the dialog element itself, not inner content)
      if (target instanceof HTMLDialogElement && target.matches(VARIANT_DIALOG_SELECTOR)) {
        this.closeVariantDialog(target);
        return;
      }

      // Close / Confirm buttons inside dialog
      const dialogClose = target.closest(DIALOG_CLOSE_SELECTOR);
      if (dialogClose) {
        const dialog = dialogClose.closest(VARIANT_DIALOG_SELECTOR);
        if (dialog instanceof HTMLDialogElement) this.closeVariantDialog(dialog);
        return;
      }

      const dialogConfirm = target.closest(DIALOG_CONFIRM_SELECTOR);
      if (dialogConfirm) {
        const dialog = dialogConfirm.closest(VARIANT_DIALOG_SELECTOR);
        if (dialog instanceof HTMLDialogElement) this.closeVariantDialog(dialog);
        return;
      }

      const selectedRemoveButton = target.closest(SUMMARY_REMOVE_SELECTOR);
      if (selectedRemoveButton instanceof HTMLElement) {
        event.preventDefault();
        const productId = parseIntSafe(selectedRemoveButton.dataset.bundleProductId, 0);
        if (productId <= 0) return;

        const item = this.getItemByProductId(productId);
        if (!item) return;

        const includeInput = item.querySelector(INCLUDE_INPUT_SELECTOR);
        if (!(includeInput instanceof HTMLInputElement) || includeInput.disabled || !includeInput.checked) return;

        const currentQty = this.getItemQuantity(item);
        if (currentQty > 1) {
          this.setItemQuantity(item, currentQty - 1);
          this.updateSummary({ preserveStatus: false });
        } else {
          this.toggleItemByProductId(productId, false);
        }
        return;
      }

      const quantityButton = target.closest(`${QUANTITY_DECREASE_SELECTOR}, ${QUANTITY_INCREASE_SELECTOR}`);
      if (quantityButton instanceof HTMLElement) {
        event.preventDefault();
        const item = quantityButton.closest(ITEM_SELECTOR);
        if (!item) return;

        const includeInput = item.querySelector(INCLUDE_INPUT_SELECTOR);
        if (!(includeInput instanceof HTMLInputElement) || includeInput.disabled) return;

        const isDecrease = quantityButton.matches(QUANTITY_DECREASE_SELECTOR);

        if (!includeInput.checked) {
          if (isDecrease) return;
          includeInput.checked = true;
          this.setItemQuantity(item, 1);
          includeInput.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const currentQty = this.getItemQuantity(item);
        if (isDecrease) {
          if (currentQty > 1) {
            this.setItemQuantity(item, currentQty - 1);
            this.updateSummary({ preserveStatus: false });
          } else {
            includeInput.checked = false;
            includeInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        this.setItemQuantity(item, currentQty + 1);
        this.updateSummary({ preserveStatus: false });
        return;
      }

      const toggleButton = target.closest(TOGGLE_BUTTON_SELECTOR);
      if (toggleButton) {
        event.preventDefault();
        const item = toggleButton.closest(ITEM_SELECTOR);
        if (!item) return;

        const includeInput = item.querySelector(INCLUDE_INPUT_SELECTOR);
        if (!(includeInput instanceof HTMLInputElement) || includeInput.disabled) return;

        if (!includeInput.checked) {
          includeInput.checked = true;
          this.setItemQuantity(item, 1);
          includeInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const submitButton = target.closest(SUBMIT_SELECTOR);
      if (submitButton) {
        event.preventDefault();
        this.submitBundle();
      }
    }

    getItemByProductId(productId) {
      if (!productId) return null;
      return (
        this.items.find((item) => parseIntSafe(item.dataset.productId, 0) === productId) ||
        null
      );
    }

    toggleItemByProductId(productId, included) {
      const item = this.getItemByProductId(productId);
      if (!item) return;

      const includeInput = item.querySelector(INCLUDE_INPUT_SELECTOR);
      if (!(includeInput instanceof HTMLInputElement) || includeInput.disabled) return;
      includeInput.checked = Boolean(included);
      if (includeInput.checked) {
        this.setItemQuantity(item, this.getItemQuantity(item));
      } else {
        item.removeAttribute('data-bundle-quantity-count');
      }
      includeInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    collectSelectedLines() {
      const lines = [];
      let selectedCount = 0;
      let totalCents = 0;
      let compareAtTotalCents = 0;

      this.items.forEach((item) => {
        const state = this.itemState.get(item);
        const variant = state?.currentVariant;
        const includeInput = item.querySelector(INCLUDE_INPUT_SELECTOR);

        const isIncluded = includeInput ? includeInput.checked && !includeInput.disabled : Boolean(variant?.available);
        const variantId = parseIntSafe(variant?.id, parseIntSafe(item.dataset.selectedVariantId, 0));
        const variantAvailable = Boolean(variant?.available);

        if (!isIncluded || !variantId || !variantAvailable) return;

        const quantity = this.getItemQuantity(item);

        const priceCents = Math.max(0, parseIntSafe(variant?.price, parseIntSafe(item.dataset.selectedPriceCents, 0)));
        const compareAtCents = this.normalizeCompareAt(
          priceCents,
          parseIntSafe(variant?.compare_at_price, parseIntSafe(item.dataset.selectedCompareCents, priceCents))
        );

        selectedCount += 1;
        totalCents += priceCents * quantity;
        compareAtTotalCents += compareAtCents * quantity;

        const variantParts = this.getVariantOptions(variant).filter(Boolean);

        lines.push({
          id: variantId,
          productId: parseIntSafe(item.dataset.productId, 0),
          quantity,
          title: item.dataset.bundleProductTitle || '',
          productUrl: item.dataset.bundleProductUrl || '',
          thumbUrl: item.dataset.bundleThumbUrl || '',
          variantLabel: variantParts.join(' / '),
          linePriceCents: priceCents * quantity,
        });
      });

      const savingsCents = Math.max(0, compareAtTotalCents - totalCents);

      return {
        lines,
        selectedCount,
        totalCents,
        compareAtTotalCents,
        savingsCents,
      };
    }

    updateProgress(summary) {
      this.progressTierEls.forEach((tierEl) => {
        if (!(tierEl instanceof HTMLElement)) return;
        const threshold = parseIntSafe(tierEl.dataset.bundleTier, 0);
        tierEl.classList.toggle('is-hit', summary.selectedCount >= threshold);
      });

      this.scheduleOfferProgressGeometry();
    }

    renderSelectedEmptySkeletons() {
      return `
        <div class="product-bundle__selected-empty-skeletons" aria-hidden="true">
          <div class="product-bundle__selected-empty-skeleton">
            <span class="product-bundle__selected-empty-thumb"></span>
            <span class="product-bundle__selected-empty-content">
              <span class="product-bundle__selected-empty-line product-bundle__selected-empty-line--wide"></span>
              <span class="product-bundle__selected-empty-line product-bundle__selected-empty-line--medium"></span>
              <span class="product-bundle__selected-empty-line product-bundle__selected-empty-line--short"></span>
            </span>
          </div>
          <div class="product-bundle__selected-empty-skeleton">
            <span class="product-bundle__selected-empty-thumb"></span>
            <span class="product-bundle__selected-empty-content">
              <span class="product-bundle__selected-empty-line product-bundle__selected-empty-line--wide"></span>
              <span class="product-bundle__selected-empty-line product-bundle__selected-empty-line--medium"></span>
              <span class="product-bundle__selected-empty-line product-bundle__selected-empty-line--short"></span>
            </span>
          </div>
        </div>
      `;
    }

    renderSelectedList(summary, { scrollToProductId = null } = {}) {
      if (!this.selectedListEl) return;

      if (!summary.lines.length) {
        this.selectedListEl.innerHTML = this.renderSelectedEmptySkeletons();
        this.renderSelectedPagination(0);
        this.selectedListEl.scrollTop = 0;
        this.updateSelectedListFadeState();
        return;
      }

      const markup = summary.lines
        .map((line, index) => {
          const title = escapeHtml(line.title);
          const removeLabel = line.quantity > 1 ? 'Decrease quantity' : 'Remove item';

          return `
            <div class="product-bundle__selected-item" data-selected-slide-index="${index}" data-bundle-selected-product-id="${line.productId}">
              <div class="product-bundle__selected-media">
                ${
                  line.thumbUrl
                    ? `<img class="product-bundle__selected-thumb" src="${escapeHtml(line.thumbUrl)}" alt="${escapeHtml(line.title)}" loading="lazy">`
                    : '<span class="product-bundle__selected-thumb" aria-hidden="true"></span>'
                }
                <button type="button" class="product-bundle__selected-remove product-bundle__selected-remove--icon" data-bundle-selected-remove data-bundle-product-id="${line.productId}" aria-label="${removeLabel}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></line>
                  </svg>
                </button>
              </div>
              <div class="product-bundle__selected-meta">
                <p class="product-bundle__selected-title">${title}</p>
                ${line.variantLabel ? `<p class="product-bundle__selected-variant">${escapeHtml(line.variantLabel)}</p>` : ''}
                ${line.quantity > 1 ? `<p class="product-bundle__selected-qty">Qty ${line.quantity}</p>` : ''}
                <span class="product-bundle__selected-price">${this.formatMoney(line.linePriceCents)}</span>
              </div>
            </div>
          `;
        })
        .join('');

      this.selectedListEl.innerHTML = `<div class="product-bundle__selected-track">${markup}</div>`;
      this.renderSelectedPagination(0);
      this.updateSelectedListFadeState();
      if (scrollToProductId) {
        this.scrollSelectedItemIntoView(scrollToProductId);
      }
    }

    renderSelectedPagination(slidesCount) {
      if (!this.selectedPaginationEl) return;

      this.selectedPaginationEl.innerHTML = '';
      this.selectedPaginationEl.hidden = true;
    }

    destroySelectedSwiper() {
      if (this.selectedSwiper) {
        this.selectedSwiper.destroy(true, true);
        this.selectedSwiper = null;
      }
      if (this.selectedListEl) {
        this.selectedListEl.classList.remove('swiper-initialized');
      }
    }

    updateSummary({ preserveStatus = false } = {}) {
      const summary = this.collectSelectedLines();
      let addedProductId = null;

      if (this.hasRenderedSummaryOnce && summary.selectedCount > this.previousSelectedProductIds.size) {
        for (const line of summary.lines) {
          if (!this.previousSelectedProductIds.has(line.productId)) {
            addedProductId = line.productId;
            break;
          }
        }
      }

      this.summaryCountEls.forEach((element) => {
        element.textContent = String(summary.selectedCount);
      });

      this.summaryTotalEls.forEach((element) => {
        element.innerHTML = this.formatMoney(summary.totalCents);
      });

      this.summaryFinalEls.forEach((element) => {
        element.innerHTML = this.formatMoney(summary.totalCents);
      });

      this.summaryCompareEls.forEach((element) => {
        element.innerHTML = this.formatMoney(summary.compareAtTotalCents);
      });

      this.summarySavingsEls.forEach((element) => {
        element.innerHTML = this.formatMoney(summary.savingsCents);
      });

      const showCompareRow =
        this.showCompareAtTotal &&
        summary.compareAtTotalCents > 0 &&
        summary.compareAtTotalCents > summary.totalCents;
      this.summaryCompareRows.forEach((row) => {
        row.hidden = !showCompareRow;
      });

      const showSavingsRow = this.showSavings && summary.savingsCents > 0;
      this.summarySavingsRows.forEach((row) => {
        row.hidden = !showSavingsRow;
      });

      this.renderSelectedList(summary, { scrollToProductId: addedProductId });
      this.updateProgress(summary);
      this.previousSelectedProductIds = new Set(summary.lines.map((line) => line.productId));
      this.hasRenderedSummaryOnce = true;

      const shouldDisableSubmit =
        this.isSubmitting ||
        summary.selectedCount < this.minimumRequired ||
        summary.lines.length === 0;

      this.submitButtons.forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;
        button.disabled = shouldDisableSubmit;
        button.setAttribute('aria-disabled', String(shouldDisableSubmit));
      });

      if (!preserveStatus && this.hasAttemptedSubmit && !this.isSubmitting) {
        if (summary.selectedCount < this.minimumRequired) {
          this.setStatus(this.minItemsMessage);
        } else {
          this.setStatus('');
        }
      }

      return summary;
    }

    async submitBundle() {
      if (this.isSubmitting) return;

      this.hasAttemptedSubmit = true;
      const summary = this.updateSummary({ preserveStatus: false });

      if (summary.selectedCount < this.minimumRequired || !summary.lines.length) {
        this.setStatus(this.minItemsMessage || this.errorMessage);
        return;
      }

      this.isSubmitting = true;
      this.root.setAttribute('aria-busy', 'true');
      this.setStatus(this.addingMessage);

      this.submitButtons.forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      });

      try {
        const response = await fetch(`${this.routesRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: summary.lines.map((line) => ({ id: line.id, quantity: line.quantity })),
          }),
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.description || this.errorMessage);
        }

        this.setStatus(this.addedMessage);

        const onCartPage = document.body.dataset.pageType === 'cart';
        if (this.cartType === 'page' || onCartPage) {
          window.location.href = this.cartUrl;
          return;
        }

        document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
        document.dispatchEvent(
          new CustomEvent('theme:cart:open', {
            detail: { opener: document.querySelector('[data-cart-trigger]') },
          })
        );
      } catch (error) {
        this.setStatus(error?.message || this.errorMessage);
      } finally {
        this.isSubmitting = false;
        this.root.removeAttribute('aria-busy');

        this.submitButtons.forEach((button) => {
          if (!(button instanceof HTMLButtonElement)) return;
          button.removeAttribute('aria-busy');
        });

        this.updateSummary({ preserveStatus: true });
      }
    }

    setStatus(message) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message || '';
    }

    formatMoney(cents) {
      if (!Number.isFinite(cents)) return '';

      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.floor(cents / 100).toString();
      const amountWithComma = amount.replace('.', ',');
      const amountNoDecimalsWithComma = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      return this.moneyFormat
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace(/\{\{amount\}\}/g, amount);
    }
  }

  const initRoot = (root) => {
    if (!root || controllers.has(root)) return;

    const controller = new ProductBundleController(root);
    if (controller.init()) {
      controllers.set(root, controller);
    }
  };

  const destroyRoot = (root) => {
    const controller = controllers.get(root);
    if (!controller) return;
    controller.destroy();
    controllers.delete(root);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  const destroyInSection = (sectionRoot) => {
    if (!sectionRoot) return;
    sectionRoot.querySelectorAll(ROOT_SELECTOR).forEach(destroyRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event.target.closest('.shopify-section') || event.target;
    if (!sectionRoot) return;

    destroyInSection(sectionRoot);
    initAll(sectionRoot);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionRoot = event.target.closest('.shopify-section') || event.target;
    destroyInSection(sectionRoot);
  });
})();
